import { InkPeople, InkScale, InkDocument, InkShield, InkHeart, InkChat, InkPhone, InkClipboard, InkBriefcase, InkCap, InkBook } from "./InkIcons";
import { useLang } from "../lib/lang";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";

const WHY_ICONS = [InkPeople, InkScale, InkDocument, InkShield, InkHeart];
const STEP_ICONS = [InkChat, InkPhone, InkClipboard];

export const WhyUs = () => {
  const { t } = useLang();
  return (
    <section className="bg-sky/60 py-24 lg:py-32" data-testid="why-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow>{t.why.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle className="max-w-2xl">{t.why.title}</SectionTitle></Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
          {t.why.items.map((w, i) => {
            const Icon = WHY_ICONS[i];
            const span = i < 2 ? "lg:col-span-3" : "lg:col-span-2";
            return (
              <Reveal key={w.title} delay={i * 0.06} className={`${span} ${i === 4 ? "sm:col-span-2 lg:col-span-2" : ""}`}>
                <div className="group h-full rounded-[1.75rem] bg-white p-7 transition-shadow duration-300 hover:shadow-[0_30px_50px_-34px_rgba(18,24,63,0.55)] sm:p-8" data-testid={`why-card-${i}`}>
                  <span className="relative flex h-14 w-14 items-center justify-center">
                    <span className="absolute inset-0 rounded-full bg-sun transition-transform duration-500 group-hover:scale-110" />
                    <Icon className="relative h-8 w-8 text-night" accent="#FFFFFF" />
                  </span>
                  <h3 className="mt-8 font-display text-xl font-extrabold text-ink sm:text-2xl">{w.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-night/75">{w.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const HowItWorks = () => {
  const { t } = useLang();
  return (
    <section id="how-it-works" className="scroll-mt-20 relative overflow-hidden bg-ink py-24 text-white lg:py-32" data-testid="how-section">
      <div className="dot-grid pointer-events-none absolute inset-0 opacity-25" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow dark>{t.how.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle dark className="max-w-2xl">{t.how.title}</SectionTitle></Reveal>
        <ol className="mt-16 grid items-end gap-5 md:grid-cols-3">
          {t.how.steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            const lift = ["md:mb-0", "md:mb-12", "md:mb-24"][i];
            return (
              <Reveal key={s.title} delay={i * 0.12} className={lift}>
                <li className={`relative rounded-[1.75rem] p-7 sm:p-9 ${i === 2 ? "bg-sun text-night" : "border border-white/15 bg-white/5 backdrop-blur"}`} data-testid={`how-step-${i + 1}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-display text-6xl font-extrabold leading-none ${i === 2 ? "text-night" : "text-sun"}`}>{i + 1}</span>
                    <span className={`flex h-14 w-14 items-center justify-center rounded-full ${i === 2 ? "bg-night text-white" : "bg-white/10 text-white"}`}><Icon className="h-8 w-8" /></span>
                  </div>
                  <h3 className={`mt-10 font-display text-xl font-extrabold sm:text-2xl ${i === 2 ? "text-night" : "text-white"}`}>{s.title}</h3>
                  <p className={`mt-2 text-base ${i === 2 ? "text-night/80" : "text-white/75"}`}>{s.text}</p>
                </li>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export const WhoFor = () => {
  const { t } = useLang();
  const icons = [InkBriefcase, InkCap, InkBook];
  return (
    <section id="who-for" className="scroll-mt-36 py-24 lg:py-32" data-testid="who-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow>{t.who.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle className="max-w-3xl">{t.who.title}</SectionTitle></Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {t.who.items.map((w, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={w.title} delay={i * 0.08}>
                <article className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-ink/10 bg-white" data-testid={`who-card-${i}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={w.img} alt={w.alt} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="px-7 pt-2 text-xs italic text-night/70" data-testid={`who-img-note-${i}`}>{t.imgNote}</p>
                  <div className="flex flex-1 flex-col p-7">
                    <Icon className="h-9 w-9 text-ink" />
                    <h3 className="mt-4 font-display text-xl font-extrabold text-ink">{w.title}</h3>
                    <p className="mt-2 text-base leading-relaxed text-night">{w.text}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
